import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  DashboardCard,
  PageHeader,
  Separator,
  StatusBadge,
  cn,
} from '@zafroi/ui-kit';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Copy,
  ExternalLink,
  Info,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  Send,
  Sparkles,
  UserSquare,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useChannels,
  useCustomer,
  useSupportRequest,
} from '@/hooks/useEngageQueries';
import { useTenant } from '@/services/tenant-context';
import {
  getSupportMessages,
  replyToSupportRequest,
  resolveSupportRequest,
} from '@/services/engage-api';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import {
  channelKindLabel,
  priorityVariants,
  supportRequestStatusVariants,
} from '@/types/status-variants';
import { relativeTime, shortDateTime } from '@/lib/format';
import type { ConversationMessage, Customer, SupportRequest } from '@/types/engage';

/** Statuses where the request is finished — the Resolve action is hidden. */
const CLOSED_STATUSES: SupportRequest['status'][] = ['resolved', 'closed'];

const RESOLUTION_NOTES_MAX = 2000;

/**
 * Derive the SR-XXXXXX reference from a request id — matches the runtime's
 * `EngageBotApiService.raiseSupportRequest` + `EngageSupportService.referenceFor`
 * which both do `SR-${id.slice(0, 6).toUpperCase()}`. Mock fixtures use
 * synthetic ids like `sr_001`; strip that prefix first so the chip reads as a
 * clean reference in mock mode too.
 */
function supportReferenceFor(id: string): string {
  const stripped = id.replace(/^sr_?/i, '').replace(/[^A-Za-z0-9]/g, '');
  return `SR-${stripped.slice(0, 6).toUpperCase()}`;
}

export function SupportRequestDetailPage() {
  const navigate = useNavigate();
  const { requestId } = useParams<{ requestId: string }>();
  const requestQuery = useSupportRequest(requestId);
  const customerQuery = useCustomer(requestQuery.data?.customerId);

  return (
    <QueryStateGuard
      data={requestQuery.data}
      isLoading={requestQuery.isLoading}
      isError={requestQuery.isError}
      emptyTitle="Support request not found"
    >
      {(req) => (
        <div className="space-y-6">
          <PageHeader
            eyebrow={
              <button
                type="button"
                className="inline-flex items-center gap-1 hover:text-foreground"
                onClick={() => navigate('/support')}
              >
                <ArrowLeft className="h-3 w-3" />
                Support
              </button>
            }
            title={req.subject}
            description={
              <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                <span>{req.customerName}</span>
                <span aria-hidden className="text-muted-foreground/60">
                  ·
                </span>
                <ReferenceChip reference={supportReferenceFor(req.id)} />
              </span>
            }
            meta={
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  Status
                  <StatusBadge
                    status={req.status}
                    variants={supportRequestStatusVariants}
                  />
                </span>
                <Separator orientation="vertical" className="h-4" />
                <span className="inline-flex items-center gap-1.5">
                  Priority
                  <StatusBadge
                    status={req.priority}
                    variants={priorityVariants}
                  />
                </span>
                <Separator orientation="vertical" className="h-4" />
                <span className="inline-flex items-center gap-1.5">
                  Channel
                  <Badge tone="neutral" withDot={false}>
                    {channelKindLabel[req.channel] ?? req.channel}
                  </Badge>
                </span>
                <Separator orientation="vertical" className="h-4" />
                <span>
                  Created{' '}
                  <span className="text-foreground">
                    {shortDateTime(req.createdAt)}
                  </span>
                </span>
              </div>
            }
          />

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <ConversationPanel
                request={req}
                customer={customerQuery.data ?? null}
              />
            </div>
            <CustomerCard
              customer={customerQuery.data ?? null}
              fallbackName={req.customerName}
              onOpenProfile={(id) => navigate(`/customers/${id}`)}
            />
          </div>

          {CLOSED_STATUSES.includes(req.status) ? (
            <ResolutionSummary request={req} />
          ) : (
            <ResolvePanel request={req} />
          )}
        </div>
      )}
    </QueryStateGuard>
  );
}

/**
 * Copy-to-clipboard chip for the SR-XXXXXX reference. Agents share this
 * reference verbally and in tickets; making it one-click copyable is high-
 * value polish. Renders as a keyboard-accessible <button> so it stays
 * focusable in the page-header subtitle.
 */
function ReferenceChip({ reference }: { reference: string }) {
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(reference);
      toast.success(`Copied ${reference}`);
    } catch {
      toast.error("Couldn't copy to clipboard.");
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      title={`Copy ${reference}`}
      aria-label={`Copy support reference ${reference}`}
      className="inline-flex items-center gap-1 rounded-sm border border-border bg-muted/60 px-1.5 py-0.5 font-mono text-[11px] font-medium text-foreground transition hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
    >
      <span>{reference}</span>
      <Copy className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
    </button>
  );
}

/**
 * Customer sidebar card — glanceable identity for the person on the other
 * end of the thread. Name + (company or Individual), then email/phone when
 * present, and an Open profile action that deep-links into the full
 * customer record.
 */
function CustomerCard({
  customer,
  fallbackName,
  onOpenProfile,
}: {
  customer: Customer | null;
  fallbackName: string;
  onOpenProfile: (customerId: string) => void;
}) {
  return (
    <DashboardCard title="Customer" icon={UserSquare}>
      {customer ? (
        <div className="space-y-3">
          <div>
            <div className="text-sm font-medium text-foreground">
              {customer.name}
            </div>
            <div className="text-xs text-muted-foreground">
              {customer.company ?? 'Individual'}
            </div>
          </div>
          {(customer.email || customer.phone) && (
            <dl className="space-y-1.5 text-xs">
              {customer.email && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="truncate text-foreground">
                    {customer.email}
                  </span>
                </div>
              )}
              {customer.phone && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="truncate text-foreground">
                    {customer.phone}
                  </span>
                </div>
              )}
            </dl>
          )}
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => onOpenProfile(customer.id)}
          >
            Open profile
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-sm font-medium text-foreground">
            {fallbackName}
          </div>
          <p className="text-xs text-muted-foreground">
            Customer record unavailable.
          </p>
        </div>
      )}
    </DashboardCard>
  );
}

/**
 * Resolve action — Loop B of the self-improving knowledge base. The agent
 * writes how the issue was resolved; on confirm the runtime marks the request
 * resolved and AI-drafts a knowledge-base article from it (a slow LLM pass).
 */
function ResolvePanel({ request }: { request: SupportRequest }) {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const [isOpen, setIsOpen] = useState(false);
  const [notes, setNotes] = useState('');

  const mutation = useMutation({
    mutationFn: (resolutionNotes: string) =>
      resolveSupportRequest(request.id, resolutionNotes),
    onSuccess: ({ suggestedDraft }) => {
      // Flip the status everywhere — this request and the support list.
      queryClient.invalidateQueries({ queryKey: ['support', currentTenant.id] });
      // The drafted article was added to the knowledge base — refresh it.
      queryClient.invalidateQueries({
        queryKey: ['bot-knowledge', currentTenant.id],
      });
      if (suggestedDraft) {
        toast.success('Request resolved — knowledge article drafted', {
          description:
            'A knowledge-base article was drafted from this resolution. ' +
            'Review and approve it on the Chatbot page.',
        });
      } else {
        toast.success('Request resolved');
      }
      setIsOpen(false);
      setNotes('');
    },
    onError: () => {
      toast.error("Couldn't resolve this request — try again in a moment.");
    },
  });

  const trimmed = notes.trim();
  const canConfirm =
    trimmed.length > 0 && trimmed.length <= RESOLUTION_NOTES_MAX;

  return (
    <DashboardCard
      title="Resolve request"
      description="Close this request out and capture how it was handled."
      icon={CheckCircle2}
    >
      {mutation.isPending ? (
        <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
          <Sparkles className="h-4 w-4 shrink-0 animate-pulse text-primary" />
          <span>
            Resolving and drafting a knowledge article… this runs an AI pass,
            so it can take a few moments.
          </span>
        </div>
      ) : !isOpen ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            When you resolve a request, ZAFROI drafts a reusable knowledge-base
            article from the issue and your resolution — ready for you to review
            on the Chatbot page.
          </p>
          <Button size="sm" onClick={() => setIsOpen(true)}>
            <CheckCircle2 className="h-3.5 w-3.5" />
            Resolve request
          </Button>
        </div>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (canConfirm) mutation.mutate(trimmed);
          }}
        >
          <div className="space-y-1.5">
            <label
              htmlFor="resolution-notes"
              className="text-sm font-medium text-foreground"
            >
              How was this resolved? This becomes a knowledge-base draft.
            </label>
            <textarea
              id="resolution-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              maxLength={RESOLUTION_NOTES_MAX}
              autoFocus
              placeholder="Describe the fix in plain language — the steps you took and the outcome the customer can expect…"
              className="min-h-24 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm shadow-elev-1 placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <p className="text-[11px] text-muted-foreground">
              {trimmed.length}/{RESOLUTION_NOTES_MAX} characters
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" size="sm" disabled={!canConfirm}>
              <CheckCircle2 className="h-3.5 w-3.5" />
              Confirm resolution
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsOpen(false);
                setNotes('');
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </DashboardCard>
  );
}

/** Read-only view of how a resolved/closed request was handled. */
function ResolutionSummary({ request }: { request: SupportRequest }) {
  return (
    <DashboardCard
      title="Resolution"
      description="How this request was resolved."
      icon={CheckCircle2}
      headerActions={
        request.resolvedAt ? (
          <Badge tone="success" withDot={false}>
            Resolved {relativeTime(request.resolvedAt)}
          </Badge>
        ) : undefined
      }
    >
      {request.resolutionNotes ? (
        <p className="whitespace-pre-line text-sm text-foreground">
          {request.resolutionNotes}
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          This request was closed without a recorded resolution note.
        </p>
      )}
    </DashboardCard>
  );
}

/**
 * Conversation panel — the primary working surface for an open support
 * request. Renders the message thread (chat-style bubbles, inbound left /
 * outbound right) and, when the request is EMAIL + still open, the
 * agent-reply composer beneath it. For non-email channels and
 * resolved/closed requests we replace the composer with an inline note
 * explaining why no reply is possible right now.
 *
 * When the message thread is empty (legacy/bot-escalated requests with no
 * `conversationId`), the panel falls back to rendering the request
 * `description` as the customer's original message — so the agent always
 * sees the customer's words, regardless of where the request came from.
 */
function ConversationPanel({
  request,
  customer,
}: {
  request: SupportRequest;
  customer: Customer | null;
}) {
  const messagesQuery = useQuery({
    queryKey: ['support-messages', request.id],
    queryFn: () => getSupportMessages(request.id),
    select: (data) => data.messages,
  });

  const messages = messagesQuery.data ?? [];
  const isEmail = request.channel === 'email';
  const isClosed = CLOSED_STATUSES.includes(request.status);

  return (
    <DashboardCard
      title="Conversation"
      description="Messages between the customer and your team."
      icon={MessageSquare}
    >
      <div className="space-y-4">
        <MessageThread
          messages={messages}
          isLoading={messagesQuery.isLoading}
          isError={messagesQuery.isError}
          fallbackBody={request.description ?? null}
        />

        {isEmail && !isClosed && (
          <ReplyComposer request={request} customer={customer} />
        )}

        {!isEmail && (
          <InlineNote tone="info">
            Replies via {channelKindLabel[request.channel] ?? request.channel}{' '}
            aren’t supported yet — only email replies can be sent for now.
          </InlineNote>
        )}

        {isEmail && isClosed && (
          <InlineNote tone="muted">
            This ticket is closed. Re-open it (by receiving a new email reply)
            to continue the conversation.
          </InlineNote>
        )}
      </div>
    </DashboardCard>
  );
}

function MessageThread({
  messages,
  isLoading,
  isError,
  fallbackBody,
}: {
  messages: ConversationMessage[];
  isLoading: boolean;
  isError: boolean;
  /** When there's no thread, render this as the customer's original
   * message — keeps the visual continuity with the chat-bubble pattern
   * for requests that never had a conversation attached. */
  fallbackBody?: string | null;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading messages…
      </div>
    );
  }
  if (isError) {
    return (
      <InlineNote tone="warning">
        Couldn’t load the conversation thread. Refresh and try again.
      </InlineNote>
    );
  }
  if (messages.length === 0) {
    if (fallbackBody && fallbackBody.trim().length > 0) {
      return (
        <div className="space-y-2">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Customer’s original message (no thread on this request)
          </p>
          <div className="flex w-full justify-start">
            <div className="flex max-w-[70%] flex-col gap-1 items-start">
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <span className="font-medium text-foreground">Customer</span>
              </div>
              <div className="whitespace-pre-wrap break-words rounded-2xl rounded-bl-md border border-border bg-muted/40 px-3.5 py-2 text-sm text-foreground shadow-elev-1">
                {fallbackBody}
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="rounded-md border border-dashed border-border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
        No conversation yet. This ticket has no message thread.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {messages.map((m) => (
        <SupportMessageBubble key={m.id} message={m} />
      ))}
    </div>
  );
}

function SupportMessageBubble({ message }: { message: ConversationMessage }) {
  const outbound = message.direction === 'outbound';
  // Auto-respond messages are outbound but should read as system, not agent —
  // the tenant needs to tell at-a-glance that no human has replied yet.
  const isAutoRespond =
    outbound &&
    typeof message.authorName === 'string' &&
    /auto[-\s]?respond/i.test(message.authorName);
  const author =
    message.authorName ??
    (outbound ? 'Your team' : 'Customer');
  return (
    <div
      className={cn('flex w-full', outbound ? 'justify-end' : 'justify-start')}
    >
      <div className={cn('flex max-w-[70%] flex-col gap-1', outbound && 'items-end')}>
        <div
          className={cn(
            'flex items-center gap-1.5 text-[11px] text-muted-foreground',
            outbound && 'flex-row-reverse',
          )}
        >
          {!outbound && message.channel === 'email' && (
            <Mail className="h-3 w-3 shrink-0" aria-hidden />
          )}
          <span
            className={cn(
              'font-medium',
              isAutoRespond ? 'text-info' : 'text-foreground',
            )}
          >
            {author}
          </span>
          {isAutoRespond && (
            <span className="rounded-sm border border-info/40 bg-info/10 px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-info">
              Auto
            </span>
          )}
          <span aria-hidden>·</span>
          <span title={shortDateTime(message.at)}>{relativeTime(message.at)}</span>
        </div>
        <div
          className={cn(
            'whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2 text-sm shadow-elev-1',
            outbound
              ? isAutoRespond
                ? 'rounded-br-md border border-info/30 bg-info/10 text-foreground'
                : 'rounded-br-md bg-primary text-primary-foreground'
              : 'rounded-bl-md border border-border bg-muted/40 text-foreground',
          )}
        >
          {message.body}
        </div>
      </div>
    </div>
  );
}

/** Multiline agent reply composer — only mounted when the channel supports
 * replies AND the request is still open. */
function ReplyComposer({
  request,
  customer,
}: {
  request: SupportRequest;
  customer: Customer | null;
}) {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const channelsQuery = useChannels();
  const [draft, setDraft] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const mutation = useMutation({
    mutationFn: (body: string) => replyToSupportRequest(request.id, body),
    onSuccess: () => {
      setDraft('');
      // Pull the freshly-persisted thread (now includes our outbound reply).
      queryClient.invalidateQueries({
        queryKey: ['support-messages', request.id],
      });
      // Status may have flipped PENDING/TRIAGED -> IN_PROGRESS — refresh both
      // the detail query and the support list so the change is visible
      // everywhere.
      queryClient.invalidateQueries({
        queryKey: ['support', currentTenant.id, request.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['support', currentTenant.id],
      });
      toast.success('Reply sent.');
    },
    onError: (error: Error) => {
      // The runtime returns user-friendly messages — surface them verbatim.
      toast.error(error.message || "Couldn't send your reply.");
    },
  });

  // Auto-focus when the composer mounts on an open request so the agent can
  // start typing immediately.
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const trimmed = draft.trim();
  const canSend = trimmed.length > 0 && !mutation.isPending;

  const emailChannel = (channelsQuery.data ?? []).find(
    (c) => c.kind === 'email',
  );
  const fromIdentity =
    emailChannel?.identifier?.trim() || 'your connected Gmail';
  const toIdentity = customer?.email?.trim() || `${request.customerName}`;
  const customerFirstName =
    (customer?.name ?? request.customerName).split(' ')[0] || 'the customer';

  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSend) mutation.mutate(trimmed);
      }}
    >
      <label htmlFor="support-reply" className="sr-only">
        Reply to customer
      </label>
      <textarea
        id="support-reply"
        ref={textareaRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={6}
        placeholder={`Write your reply to ${customerFirstName}…`}
        className="min-h-32 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm shadow-elev-1 placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        disabled={mutation.isPending}
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {trimmed.length} characters
        </span>
        <Button
          type="submit"
          size="sm"
          disabled={!canSend}
          aria-label="Send reply"
        >
          {mutation.isPending ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Sending…
            </>
          ) : (
            <>
              <Send className="h-3.5 w-3.5" />
              Send reply
            </>
          )}
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Sends from{' '}
        <span className="font-medium text-foreground">{fromIdentity}</span> to{' '}
        <span className="font-medium text-foreground">{toIdentity}</span>. The
        customer will see your reply on the same email thread.
      </p>
    </form>
  );
}

/** Inline note rendered where the composer would be when reply isn't
 * available — non-email channels and resolved/closed requests. Tones map
 * to the surrounding palette: info (blue) for "not supported yet",
 * warning (amber) for thread-load failures, muted for closed tickets. */
function InlineNote({
  tone,
  children,
}: {
  tone: 'info' | 'warning' | 'muted';
  children: React.ReactNode;
}) {
  const Icon = tone === 'warning' ? AlertTriangle : Info;
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-xs',
        tone === 'info' && 'border-info/30 bg-info/10 text-foreground',
        tone === 'warning' &&
          'border-warning/30 bg-warning/10 text-foreground',
        tone === 'muted' &&
          'border-border bg-muted/30 text-muted-foreground',
      )}
      role={tone === 'warning' ? 'alert' : undefined}
    >
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="leading-relaxed">{children}</span>
    </div>
  );
}
